import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { api } from '../services/api';

const ProfileContext = createContext();

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

export const ProfileProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.userId || user?.id;
  
  const [profile, setProfile] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [healthProfile, setHealthProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Load all profile data
  const loadProfileData = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Load pregnancy profile
      try {
        const profileData = await api.getPregnancyProfile(userId);
        setProfile(profileData);
      } catch (error) {
        console.warn('Could not load pregnancy profile:', error);
        setProfile(null);
      }

      // Load user preferences (optional - may not exist for new users)
      try {
        const prefsData = await api.getUserPreferences(userId);
        setPreferences(prefsData);
      } catch (error) {
        // Preferences are optional - silently handle 404s and other errors
        if (error.status === 404 || error.errorCode === 'NOT_FOUND' || error.message?.includes('not found')) {
          // Expected when preferences don't exist yet
          setPreferences(null);
        } else {
          // Only log non-404 errors
          console.warn('Could not load user preferences:', error);
          setPreferences(null);
        }
      }

      // Load health profile summary (optional - may not exist for all users)
      try {
        const healthData = await api.getHealthProfileSummary(userId);
        setHealthProfile(healthData);
      } catch (error) {
        // Health profile is optional - silently handle 404s and other errors
        if (error.status === 404 || error.errorCode === 'NOT_FOUND') {
          // Expected when health profile doesn't exist yet
          setHealthProfile(null);
        } else {
          // Only log non-404 errors
          console.warn('Could not load health profile summary:', error);
          setHealthProfile(null);
        }
      }

      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading profile data:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Load on mount and when userId changes
  useEffect(() => {
    loadProfileData();
  }, [loadProfileData]);

  // Listen for profile update events — one refetch per event
  useEffect(() => {
    const handleProfileUpdate = () => loadProfileData();
    const handlePreferencesUpdate = () => loadProfileData();
    const handleUserUpdate = () => loadProfileData();

    // Listen for profile, preferences, and user updates
    window.addEventListener('profile-updated', handleProfileUpdate);
    window.addEventListener('preferences-updated', handlePreferencesUpdate);
    window.addEventListener('user-updated', handleUserUpdate);
    
    return () => {
      window.removeEventListener('profile-updated', handleProfileUpdate);
      window.removeEventListener('preferences-updated', handlePreferencesUpdate);
      window.removeEventListener('user-updated', handleUserUpdate);
    };
  }, [loadProfileData]);

  // Aggregated profile data for easy access
  const aggregatedProfile = {
    // Allergies
    allergies: preferences?.allergies || [],
    
    // Dietary restrictions
    dietaryRestrictions: preferences?.dietaryRestrictions || profile?.dietaryRestrictions,
    
    // Medical conditions
    medicalConditions: preferences?.medicalConditions || profile?.preexistingConditions,
    complications: preferences?.complications,
    
    // Health metrics
    currentWeight: preferences?.currentWeight,
    height: preferences?.height,
    targetWeight: preferences?.targetWeight,
    bmi: healthProfile?.bmi,
    bmiCategory: healthProfile?.bmiCategory,
    
    // Activity & lifestyle
    activityLevel: preferences?.activityLevel || profile?.activityLevel,
    
    // Pregnancy experience
    morningSicknessSeverity: preferences?.morningSicknessSeverity,
    energyLevel: preferences?.energyLevel,
    cookingTime: preferences?.cookingTime,
    cookingSkill: preferences?.cookingSkill,
    
    // Food preferences
    foodPreferences: preferences?.foodPreferences || [],
    favoriteFoods: preferences?.favoriteFoods,
    dislikedFoods: preferences?.dislikedFoods,
    
    // Pregnancy info
    currentWeek: profile?.currentWeek,
    trimester: profile?.trimester,
    lmpDate: profile?.lmpDate,
    eddDate: profile?.eddDate,
  };

  // Helper functions
  const checkAllergy = (foodName, ingredients = []) => {
    if (!aggregatedProfile.allergies || aggregatedProfile.allergies.length === 0) {
      return { hasAllergy: false, allergens: [] };
    }

    const foodText = (foodName || '').toLowerCase();
    const ingredientsText = Array.isArray(ingredients) 
      ? ingredients.join(' ').toLowerCase() 
      : (ingredients || '').toLowerCase();
    const combinedText = `${foodText} ${ingredientsText}`;

    const foundAllergens = aggregatedProfile.allergies.filter(allergy => {
      const allergyLower = allergy.toLowerCase();
      return combinedText.includes(allergyLower) || 
             foodText.includes(allergyLower) ||
             ingredientsText.includes(allergyLower);
    });

    return {
      hasAllergy: foundAllergens.length > 0,
      allergens: foundAllergens
    };
  };

  const checkDietaryRestriction = (meal) => {
    if (!aggregatedProfile.dietaryRestrictions || aggregatedProfile.dietaryRestrictions === 'NONE') {
      return { violates: false, reason: null };
    }

    const restriction = aggregatedProfile.dietaryRestrictions.toLowerCase();
    const mealText = JSON.stringify(meal).toLowerCase();

    // Basic checks for common restrictions
    if (restriction.includes('vegetarian') || restriction.includes('vegan')) {
      const meatKeywords = ['meat', 'chicken', 'beef', 'pork', 'fish', 'seafood', 'turkey', 'lamb'];
      const hasMeat = meatKeywords.some(keyword => mealText.includes(keyword));
      if (hasMeat) {
        return { violates: true, reason: 'Contains meat products' };
      }
    }

    if (restriction.includes('vegan')) {
      const dairyKeywords = ['milk', 'cheese', 'yogurt', 'butter', 'cream', 'dairy'];
      const hasDairy = dairyKeywords.some(keyword => mealText.includes(keyword));
      if (hasDairy) {
        return { violates: true, reason: 'Contains dairy products' };
      }
    }

    if (restriction.includes('gluten')) {
      const glutenKeywords = ['wheat', 'bread', 'pasta', 'flour', 'gluten'];
      const hasGluten = glutenKeywords.some(keyword => mealText.includes(keyword));
      if (hasGluten) {
        return { violates: true, reason: 'Contains gluten' };
      }
    }

    return { violates: false, reason: null };
  };

  const checkMedicalCondition = (meal) => {
    if (!aggregatedProfile.medicalConditions) {
      return { requiresAttention: false, warnings: [] };
    }

    const conditions = aggregatedProfile.medicalConditions.toLowerCase();
    const mealText = JSON.stringify(meal).toLowerCase();
    const warnings = [];

    // Diabetes - check for high sugar/carb content
    if (conditions.includes('diabetes') || conditions.includes('diabetic')) {
      const highSugarKeywords = ['sugar', 'sweet', 'candy', 'dessert', 'cake', 'cookie'];
      const hasHighSugar = highSugarKeywords.some(keyword => mealText.includes(keyword));
      if (hasHighSugar) {
        warnings.push('High sugar content - monitor blood glucose levels');
      }
    }

    // Hypertension - check for high sodium
    if (conditions.includes('hypertension') || conditions.includes('high blood pressure')) {
      const highSodiumKeywords = ['sodium', 'salt', 'salted', 'processed'];
      const hasHighSodium = highSodiumKeywords.some(keyword => mealText.includes(keyword));
      if (hasHighSodium) {
        warnings.push('High sodium content - monitor blood pressure');
      }
    }

    return {
      requiresAttention: warnings.length > 0,
      warnings
    };
  };

  const refreshProfile = () => {
    loadProfileData();
  };

  const value = {
    profile,
    preferences,
    healthProfile,
    aggregatedProfile,
    loading,
    lastUpdated,
    checkAllergy,
    checkDietaryRestriction,
    checkMedicalCondition,
    refreshProfile
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};

export default ProfileContext;


